import React, { useState } from "react";
import { FlexColumn, FlexRow } from "@/ui/FlexContainer/FlexContainer";
import Typography from "@/ui/Typography/Typography";
import Button from "@/ui/Button/Button";
import { CopyIcon, QrIcon } from "@/assets/icons";
import classNames from "classnames";
import { useUserInfoStore } from "@/store/userInfoStore";
import type { Trigger } from "@/analytics";
import { RegistrationScreen } from "@/components/RegistrationScreen/RegistrationScreen";
import Modal from "@/ui/Modal/Modal";
import { QrSection } from "@/components/InviteFriend/QrSection";
import { useReferalsDataContext } from "@/store/ReferalsProvider";
import { useReferalProgramsDataContext } from "@/store/ReferalProgramsProvider";
import { useMultiplierDataContext } from "@/store/MultiplierProvider";
import {
  activeReferalProgramsThisDay,
  maxAmountRefProgramByCategory,
} from "@/utils/helpers";
import cls from "./styles.module.scss";

interface LinkProps {
  variant: "delivery" | "taxi" | "eats";
  inConditions?: boolean;
}

const COPY_TRIGGERS: Record<
  "invite" | "conditions",
  Record<LinkProps["variant"], Trigger>
> = {
  invite: {
    delivery: "ref.invite.copy_delivery",
    taxi: "ref.invite.copy_taxi",
    eats: "ref.invite.copy_eats",
  },
  conditions: {
    delivery: "ref.conditions.copy_delivery",
    taxi: "ref.conditions.copy_taxi",
    eats: "ref.conditions.copy_eats",
  },
};

const SHARE_TRIGGERS: Record<
  "invite" | "conditions",
  Record<LinkProps["variant"], Trigger>
> = {
  invite: {
    delivery: "ref.invite.share_delivery",
    taxi: "ref.invite.share_taxi",
    eats: "ref.invite.share_eats",
  },
  conditions: {
    delivery: "ref.conditions.share_delivery",
    taxi: "ref.conditions.share_taxi",
    eats: "ref.conditions.share_eats",
  },
};

const SERVICE_CONFIG = {
  delivery: {
    title: "📦 Богатый Курьер",
    description: "Доставку",
    botName: "BogatiyKurierBot",
    style: "red",
  },
  taxi: {
    title: "🚕 Богатый Таксист",
    description: "Такси",
    botName: "BogatiyTaxistBot",
    style: "yellow",
  },
  eats: {
    title: "🍕 Богатый Курьер Еда",
    description: "Богатый Курьер Еда",
    botName: "BK_EdaBot",
    style: "yellow",
  },
};

export const InviteFriendBlock = ({
  variant,
  inConditions = false,
}: LinkProps) => {
  const config = SERVICE_CONFIG[variant];
  const yandexId = useUserInfoStore((s) => s.yandexId);
  const { inviteText } = useReferalsDataContext();
  const { referalPrograms } = useReferalProgramsDataContext();
  const { multiplierInfo } = useMultiplierDataContext();
  const filteredReferalPrograms = activeReferalProgramsThisDay(referalPrograms);
  const maxAmount = maxAmountRefProgramByCategory(
    filteredReferalPrograms,
    variant,
    multiplierInfo?.current.multiplier ?? 1
  );
  const triggerScope = inConditions ? "conditions" : "invite";
  const copyTrigger = COPY_TRIGGERS[triggerScope][variant];
  const shareTrigger = SHARE_TRIGGERS[triggerScope][variant];

  const [
    isRegistrationScreenOpen,
    setIsRegistrationScreenOpen,
  ] = useState<boolean>(false);
  const [isQrModalOpen, setIsQrModalOpen] = useState<boolean>(false);

  const copyLinkUtm = inConditions ? "RTMA" : "RMMA";
  const [inviteLink, setInviteLink] = useState<string>(
    `https://t.me/${config.botName}?start=yr_${yandexId}_${copyLinkUtm}`
  );

  const [copied, setCopied] = useState(false);
  const copyLink = async () => {
    if (!yandexId) {
      setIsRegistrationScreenOpen(true);
      return;
    }

    if (copied) return;
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      setInviteLink("Скопировано, отправь другу!");
    } catch (e) {
      console.error("Ошибка при копировании: ", e);
    }
  };

  const shareLink = () => {
    if (!yandexId) {
      setIsRegistrationScreenOpen(true);
      return;
    }
    const inviteLinkUtm = inConditions ? "SMTMA" : "SMMA";

    const link = `https://t.me/${config.botName}?start=yr_${yandexId}_${inviteLinkUtm}`;
    const shareText = inConditions
      ? inviteText?.condition[variant]
      : inviteText?.main[variant];

    const encodedText = encodeURIComponent(`${shareText} \n\n ${link}`);

    window.open(`https://t.me/share/url?url=${encodedText}`, "_blank");
  };

  const openQrModal = () => {
    if (!yandexId) {
      setIsRegistrationScreenOpen(true);
      return;
    }

    setIsQrModalOpen(true);
  };

  return (
    <>
      <Modal isOpen={isQrModalOpen} onClose={() => setIsQrModalOpen(false)}>
        <QrSection
          inConditions={inConditions}
          variant={variant}
          onClose={() => setIsQrModalOpen(false)}
        />
      </Modal>

      <RegistrationScreen isOpen={isRegistrationScreenOpen} />

      <FlexColumn
        gap={8}
        className={classNames(cls.wrapper, cls[config.style])}
      >
        <Typography variant="h2">{config.title}</Typography>
        {!inConditions && (
          <Typography variant="body2" className={cls.desc}>
            Зови друзей в {config.description} —{" "}
            <span>{maxAmount.toLocaleString("ru-RU")} ₽</span>
          </Typography>
        )}

        <FlexRow
          gap={10}
          justifyContent="space-between"
          alignItems="stretch"
          className={cls.linkBlock}
        >
          <div className={classNames(cls.inviteLink)}>
            <p className={!yandexId ? cls.inviteLinkBlur : undefined}>
              {inviteLink}
            </p>
          </div>

          <button
            type="button"
            data-trigger={copyTrigger}
            className={cls.copyBtn}
            onClick={copyLink}
            disabled={copied}
          >
            <CopyIcon />
          </button>

          <button
            type="button"
            data-trigger="ref.invite.qr"
            className={cls.copyBtn}
            onClick={openQrModal}
          >
            <QrIcon />
          </button>
        </FlexRow>

        <Button
          trigger={shareTrigger}
          label="Пригласить друга"
          variant={config.style}
          onClick={shareLink}
        />
      </FlexColumn>
    </>
  );
};
